import {
  EventoDtoSchema,
  EventoListResponseSchema,
  DashboardCountsSchema,
  AuditListResponseSchema,
  FilaListResponseSchema,
  TimelineResponseSchema,
  EquipeResponseSchema,
  EquipePageResponseSchema,
  EventoPublicDtoSchema,
  PublicLinkResponseSchema,
  AjustarValorVagaResponseSchema,
  type AjustarValorVagaInput,
  type AjustarValorVagaResponse,
  type CreateEventoInput,
  type UpdateEventoInput,
  type EditPublicadoInput,
  type EventoDto,
  type EventoListResponse,
  type DashboardCounts,
  type EventoStatusFilter,
  type AuditListResponse,
  type FilaListResponse,
  type TimelineResponse,
  type EquipeResponse,
  type EquipePageResponse,
  type EventoPublicDto,
  type PublicLinkResponse,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Typed wrapper over the eventos REST surface. Constructed once per ApiClient instance
 * and exposed as `apiClient.eventos.*`. All inputs use Zod-derived types from
 * `@lance-x/shared`, the same schemas backend uses for validation (defense in depth).
 *
 * IMPORTANT: per-method route order is identical to the controller (Get('me') and
 * Get('dashboard') come before Get(':id')).
 */
export class EventosEndpoints {
  constructor(private readonly client: ApiClient) {}

  create(input: CreateEventoInput): Promise<EventoDto> {
    return this.client.post('/eventos', input, {
      responseSchema: EventoDtoSchema,
    });
  }

  findByEmpresa(filter?: {
    status?: EventoStatusFilter;
    limit?: number;
  }): Promise<EventoListResponse> {
    // Build query record dropping undefined keys; ApiClient.request already skips
    // undefined-valued params, but type-narrow here for clarity.
    const query: Record<string, string | number | undefined> = {};
    if (filter?.status !== undefined) query['status'] = filter.status;
    if (filter?.limit !== undefined) query['limit'] = filter.limit;
    return this.client.get('/eventos/me', {
      query,
      responseSchema: EventoListResponseSchema,
    });
  }

  findOne(id: string): Promise<EventoDto> {
    return this.client.get(`/eventos/${id}`, {
      responseSchema: EventoDtoSchema,
    });
  }

  updateOwn(id: string, input: UpdateEventoInput): Promise<EventoDto> {
    return this.client.patch(`/eventos/${id}`, input, {
      responseSchema: EventoDtoSchema,
    });
  }

  publicar(id: string): Promise<EventoDto> {
    return this.client.post(`/eventos/${id}/publicar`, undefined, {
      responseSchema: EventoDtoSchema,
    });
  }

  cancelar(id: string): Promise<EventoDto> {
    return this.client.post(`/eventos/${id}/cancelar`, undefined, {
      responseSchema: EventoDtoSchema,
    });
  }

  /**
   * Phase 27 v1.6 — REQ-SHARE-01 (D-05). GET /eventos/:id/publicLink.
   * Returns {slug, url} when evento.status ∈ {publicado, em_andamento};
   * 422 share_indisponivel_status otherwise. Auth-gated to empresa owners.
   */
  getPublicLink(id: string): Promise<PublicLinkResponse> {
    return this.client.get(`/eventos/${id}/publicLink`, {
      responseSchema: PublicLinkResponseSchema,
    });
  }

  /**
   * Phase 27 v1.6 — REQ-SHARE-01 (D-06). GET /e/:slug. PUBLIC endpoint —
   * no auth header. Returns the LGPD-whitelisted EventoPublicDto. 404 for
   * not-found / wrong status / canceled (unified rejection — T-27-12).
   *
   * NOTE: this method requires the api-client to skip the Authorization
   * header on the request. The default ApiClient sends Bearer always; if
   * a token is present in dev, the backend still accepts public routes
   * (the @Public() decorator opts the route OUT of JwtAuthGuard before
   * any auth check runs). Server treats requests with stale tokens
   * identically to anonymous — safe to send.
   */
  getPublicBySlug(slug: string): Promise<EventoPublicDto> {
    return this.client.get(`/e/${slug}`, {
      responseSchema: EventoPublicDtoSchema,
    });
  }

  // ----- Phase 9 v1.1 — EDIT-01/02/03 -----

  /**
   * EDIT-01 — Edit a publicado/pausado evento (5-field allowlist enforced by
   * shared EditPublicadoSchema). Backend rejects vagas/tipoContratacao/funcao/
   * valor with HTTP 422.
   */
  editPublicado(id: string, input: EditPublicadoInput): Promise<EventoDto> {
    return this.client.patch(`/eventos/${id}/edit`, input, {
      responseSchema: EventoDtoSchema,
    });
  }

  /**
   * Ajusta o valor de um cargo após o pagamento. Aumento → complemento
   * debitado do saldo da carteira (422 `saldo_insuficiente` + `details` se
   * faltar saldo). Redução bloqueada com candidato aprovado.
   */
  ajustarValorVaga(
    eventoId: string,
    vagaId: string,
    input: AjustarValorVagaInput,
  ): Promise<AjustarValorVagaResponse> {
    return this.client.patch(
      `/eventos/${eventoId}/vagas/${vagaId}/valor`,
      input,
      { responseSchema: AjustarValorVagaResponseSchema },
    );
  }

  /** EDIT-02 — Pause a publicado evento. Hides from profissional feed. */
  pausar(id: string): Promise<EventoDto> {
    return this.client.post(`/eventos/${id}/pausar`, undefined, {
      responseSchema: EventoDtoSchema,
    });
  }

  /** EDIT-02 — Resume a pausado evento. Re-surfaces in profissional feed. */
  retomar(id: string): Promise<EventoDto> {
    return this.client.post(`/eventos/${id}/retomar`, undefined, {
      responseSchema: EventoDtoSchema,
    });
  }

  /** EDIT-03 — Paginated audit list for an owned evento. Newest-first. */
  listAudit(
    id: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<AuditListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    return this.client.get(`/eventos/${id}/audit`, {
      query,
      responseSchema: AuditListResponseSchema,
    });
  }

  dashboard(): Promise<DashboardCounts> {
    return this.client.get('/eventos/dashboard', {
      responseSchema: DashboardCountsSchema,
    });
  }

  // ----- Phase 25 — Tab Equipe + Tab Atividades -----

  /**
   * Tab Atividades — cross-table chronological timeline. Empresa-only
   * (server-side @Roles(EMPRESA) + assertOwnership). `limit` defaults to 100
   * server-side; pass an override for short scrollback.
   */
  getTimeline(id: string, opts?: { limit?: number }): Promise<TimelineResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    return this.client.get(`/eventos/${id}/timeline`, {
      query,
      responseSchema: TimelineResponseSchema,
    });
  }

  /**
   * Tab Equipe — aprovada candidaturas grouped por função (MONTADOR > TECNICO >
   * CARREGADOR), with per-member mediaAvaliacao. Empresa-only (same gates).
   */
  getEquipe(id: string): Promise<EquipeResponse> {
    return this.client.get(`/eventos/${id}/equipe`, {
      responseSchema: EquipeResponseSchema,
    });
  }

  /**
   * Phase 32 Plan 04 — DEBT-04 cursor-paginated equipe read.
   *
   * Returns `{ groups, nextCursor }`. Pass `nextCursor` from the previous
   * response as `opts.cursor` to advance to the next page. `nextCursor null`
   * means the last page has been reached.
   *
   * Empresa-only. A tampered cursor returns 400 { code: 'invalid_cursor' }.
   */
  getEquipeCursor(
    eventoId: string,
    opts?: { cursor?: string; limit?: number },
  ): Promise<EquipePageResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    return this.client.get(`/eventos/${eventoId}/equipe/page`, {
      query,
      responseSchema: EquipePageResponseSchema,
    });
  }

  // ----- Phase 17 v1.3 — FILA-01 -----

  /**
   * List the pendente waitlist for a vaga inside an evento. Empresa-only
   * (server-side RBAC: ADMINISTRADOR | COLABORADOR | VISUALIZADOR). Cursor
   * pagination — `limit` defensively clamped 1..50, defaults to 20.
   * Each row carries the profissional snapshot + reputation aggregate + a
   * 1-indexed FIFO `posicao` across the whole queue.
   */
  listFila(
    eventoId: string,
    vagaId: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<FilaListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    return this.client.get(`/eventos/${eventoId}/vagas/${vagaId}/fila`, {
      query,
      responseSchema: FilaListResponseSchema,
    });
  }
}
